// One place that answers "which supports does this column have, and what is each one?".
//
// The *type* of a single support has always come from `classifySupport` in sx-common-ts, but the
// step around it - drop the lateral supports, order them along the member, and decide which one is
// the base - used to be re-derived at every call site (the 2D side views, the 3D views, the
// geometry table, the reducer that writes connectivity). Those copies were only mostly identical,
// which is the worst kind: e.g. the reaction-force drawing took "base" to mean `x === 0` rather
// than "lowest support", so a column whose supports do not start at zero disagreed with the
// support icon drawn next to it.
//
// Both the 2D and the 3D viewports now classify through this module, so a support drawn as pinned
// in the side view is pinned in the isometric view by construction rather than by coincidence.
import { ng } from "@common/ngds";
import { classifySupport, SupportAxis, SupportConnectivityType } from "@common/helpers/sharedTypes";

/** A support together with everything the drawing code needs to know about it. */
export interface ClassifiedSupport extends OrderedSupport {
  type: SupportConnectivityType;
}

/** A support placed along the member, before any per-axis classification. */
export interface OrderedSupport {
  support: ng.stage.Support;
  /** Position along the member axis, in the model's own direction (x grows upwards). */
  x: number;
  /** True for the lowest non-lateral support - the one standing on the ground. */
  isBaseSupport: boolean;
}

const supportX = (support: ng.stage.Support): number => support.getCoord()?.x ?? 0;

/**
 * The supports that take part in the column model: the lateral ones are excluded, since they are
 * bracing rather than supports and are drawn (if at all) by their own code.
 */
export const structuralSupports = (supports: ng.stage.Support[]): ng.stage.Support[] =>
  supports.filter((support) => !support.lateral);

/**
 * The member axis points up, so the base support is the one with the *smallest* x. Returns null
 * when there are no supports, so callers cannot accidentally compare against Infinity.
 */
export const baseSupportX = (supports: ng.stage.Support[]): number | null => {
  const structural = structuralSupports(supports);
  if (structural.length === 0) return null;
  return Math.min(...structural.map(supportX));
};

/**
 * The structural supports ordered from the top of the member down - the order the side views draw
 * them in - each tagged with whether it is the base support.
 */
export const orderedSupports = (supports: ng.stage.Support[]): OrderedSupport[] => {
  const structural = structuralSupports(supports);
  const minX = baseSupportX(structural);

  return [...structural]
    .sort((a, b) => supportX(b) - supportX(a))
    .map((support) => {
      const x = supportX(support);
      return { support, x, isBaseSupport: x === minX };
    });
};

/** Same, for a list held as REST/Redux JSON rather than as protobuf objects. */
export const orderedSupportsJson = (supports: ng.stage.Support_json[]): OrderedSupport[] =>
  orderedSupports(supports.map((support) => ng.stage.Support.fromObject(support)));

/**
 * Classifies every support for one cross-section axis, ordered from the top of the member down -
 * the order the side views draw them in.
 */
export const classifySupports = (supports: ng.stage.Support[], axis: SupportAxis): ClassifiedSupport[] =>
  orderedSupports(supports).map((entry) => ({
    ...entry,
    type: classifySupport(entry.support, axis, entry.isBaseSupport),
  }));

/** The project's supports, classified for one axis. The form every viewport works from. */
export const getSupports = (project: ng.frontend.column.Project | null, axis: SupportAxis): ClassifiedSupport[] =>
  classifySupports(project?.data?.input?.structures?.[0]?.stages?.[0]?.sups ?? [], axis);

/** Whether a given support is the base support of a JSON support list. */
export const isBaseSupportJson = (supports: ng.stage.Support_json[], support: ng.stage.Support): boolean => {
  const minX = baseSupportX(supports.map((entry) => ng.stage.Support.fromObject(entry)));
  return minX !== null && supportX(support) === minX;
};
